import React from "react";
import { Link } from "react-router-dom";

import { useMenuButtonOpenChat } from "hooks/useMenuButtonOpenChat";
import { useMenuButtonNavigateToProfile } from "hooks/useMenuButtonNavigateToProfile";
import Table from "components/Table";
import terminology from "terminology.json";

import { TableContainer } from "./PurchasesTable.styles";
import { formatPurchaseStatus } from "services/formatPurchaseStatus";
import { PaymentStatus } from "types-and-validators/PaymentStatus";
import { MenuButton } from "types-and-validators/MenuButton";

interface Props<ItemData extends CompatibleItem> {
	isMentor: boolean | undefined;
	items: ItemData[] | undefined;
	uid: string | undefined;

	cols: { key: string; heading: string }[];

	isWaiting: boolean;
	isLoading: boolean;

	onClickMarkPaid: (arg: ItemData) => void;

	sortItems: (a: ItemData, b: ItemData) => number;
	getColumnData: (item: ItemData) => Record<string, unknown>;
	getAction?: (item: ItemData) => MenuButton | undefined;
}

export type CompatibleItem = {
	displayNameGuest: string;
	displayNameHost: string;
	hostUid: string;
	guestUid: string;
	paymentStatus?: PaymentStatus;
	isAccepted?: boolean;
};

function PurchasesTable<ItemData extends CompatibleItem>({
	isMentor,
	items,
	uid,
	cols,
	getColumnData,
	getAction,
	isWaiting,
	isLoading,
	sortItems,
	onClickMarkPaid
}: Props<ItemData>) {
	const menuButtonOpenChat = useMenuButtonOpenChat();
	const menuButtonNavigateToProfile = useMenuButtonNavigateToProfile();

	// TODO:WV:20250313:Separate out meeting reschedule requests

	const rows =
		items && items.length
			? items
					/* TODO:WV:20250313: Don't include meeting reschedule requests */
					.sort(sortItems)
					.map(item => {
						const {
							displayNameGuest,
							displayNameHost,
							hostUid,
							guestUid,
							paymentStatus
						} = item;

						const awaitingPayment = paymentStatus === "pending";
						const isAccepted =
							item.isAccepted !== undefined && !!item.isAccepted;

						if (!uid) {
							throw new Error("Not logged in");
						}

						const isHost = hostUid === uid;
						const isGuest = !isHost;

						const partnersName = isHost ? displayNameGuest : displayNameHost;
						const partnersUid = isHost ? guestUid : hostUid;

						const action = (() => {
							const actionFromItem = getAction ? getAction(item) : undefined;
							if (actionFromItem) {
								return actionFromItem;
							}
							if (guestUid === uid) {
								if (awaitingPayment) {
									return {
										// TODO:WV:20260707:Improve the following behaviour (e.g. use the app alert dialogue)
										onClick: () =>
											window.alert(
												`Please contact your ${terminology.mentor} to arrange payment`
											),
										label: "Pay"
									};
								}
							}
							if (hostUid === uid) {
								if (awaitingPayment) {
									return {
										onClick: () => onClickMarkPaid(item),
										label: "Mark paid"
									};
								}
							}

							// TODO:WV:20250313:Extra buttons
							return undefined;
						})();

						return {
							data: {
								...getColumnData(item),
								partner: (
									<Link to={`/user-id/${partnersUid}`}>
										{partnersName ? partnersName : "(unknown)"}
									</Link>
								),
								...(isMentor ? { role: isHost ? "Host" : "Guest" } : {}),
								status: formatPurchaseStatus({
									awaitingPayment,
									isAccepted
								})
							},
							action,
							menu: [
								menuButtonOpenChat({
									uid: partnersUid,
									displayName: partnersName
								}),
								...(isGuest
									? [menuButtonNavigateToProfile({ uid: partnersUid })]
									: [])
							]
						};
					})
			: [];

	return (
		<TableContainer isLoading={isWaiting || isLoading}>
			<Table
				cols={cols}
				rows={rows}
				emptyText={`You have not scheduled any ${terminology.meetings}`}
				isLoading={isWaiting || isLoading}
			/>
		</TableContainer>
	);
}

export default PurchasesTable;
